import { api, unwrap, type Schemas } from '../../api/client';
import { useApiQuery } from '../../api/hooks';
import { isEmployee, useSession, useTenant } from '../../auth/session';
import { useCompanyPractice } from '../team/Accountant';

// From your accountant (PRACTICE-DESIGN.md 7): the client's side of the practice's questions and
// document requests.

export type AccountantQuestion = Schemas['ClientQuestion'];
export type AccountantDocument = Schemas['DocumentRequest'];

/**
 * Whether this company shows "From your accountant": it has a practice keeping its books, and the
 * person is one of its own users (a practice member in the client's books has the practice's pages).
 */
export function useHasAccountant(): boolean {
  const tenant = useTenant();
  const session = useSession();
  const practice = useCompanyPractice();
  return !isEmployee(tenant.role) && !session.client
    && (practice.data ?? []).some((p) => !p.via_practice && (p.state === 'active' || p.state === 'practice_managed'));
}

export function useAccountantRequests(enabled = true) {
  return useApiQuery(['accountant-requests'], async () =>
    (await unwrap(api.GET('/v1/accountant-requests'))).data as { questions: AccountantQuestion[]; documents: AccountantDocument[] },
  { enabled, staleTime: 30_000 });
}

/** Open questions and outstanding documents: the menu badge and the dashboard item. */
export function useAccountantOpenCount(): { questions: number; documents: number; practice: string | null } {
  const has = useHasAccountant();
  const requests = useAccountantRequests(has);
  const data = has ? requests.data : undefined;
  const questions = (data?.questions ?? []).filter((q) => q.status === 'open');
  const documents = (data?.documents ?? []).filter((d) => d.status === 'outstanding');
  return { questions: questions.length, documents: documents.length, practice: questions[0]?.practice_name ?? documents[0]?.practice_name ?? null };
}
